import * as Location from 'expo-location';
import { useAnalytics } from '@/components/analytics-provider';
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EraserIcon,
  FootprintsIcon,
  Layers3Icon,
  LocateFixedIcon,
  Maximize2Icon,
  Minimize2Icon,
  RotateCcwIcon,
  RouteIcon,
  RulerIcon,
  SaveIcon,
  SearchIcon,
  Share2Icon,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Modal, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RoutePlannerMap } from '@/components/routes/route-planner-map';
import type { RouteMapLayer } from '@/components/routes/route-planner-map.types';
import { RoutePlaceSearch } from '@/components/routes/route-place-search';
import { downloadRouteGpx, shareRouteGpx } from '@/lib/route-gpx';
import {
  clearRoutePlannerDraft,
  loadRoutePlannerDraft,
  saveRoutePlannerDraft,
} from '@/lib/route-planner-draft';
import { computeRoutePlan, savePlannedRoute } from '@/lib/route-planner';
import { BRAND } from '@sixam/config/brand';
import { systemNow, systemNowMs } from '@sixam/shared/clock';
import {
  formatPlannerDistanceSignificant,
  MAX_ROUTE_WAYPOINTS,
  straightLineRoutePlan,
  type ComputedRoutePlan,
  type RouteCoordinate,
  type RouteRoutingProfile,
  type RouteUnitSystem,
  type RouteWaypoint,
} from '@sixam/shared/route-planner-core';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Input } from '@sixam/ui/input';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

const BANGKOK_CENTER = { latitude: 13.7563, longitude: 100.5018 };
const MAP_LAYERS: readonly RouteMapLayer[] = ['standard', 'satellite', 'hybrid'];
const GUIDANCE_PANEL_HEIGHT = 196;

type RoutePlannerProps = Readonly<{
  draftKey?: string;
  exitLabel?: string;
  initialAutoRouting?: boolean;
  initialCenter?: RouteCoordinate;
  initialPlan?: ComputedRoutePlan | null;
  initialToolRailCompact?: boolean;
  initialWaypoints?: readonly RouteWaypoint[];
  isFullscreen?: boolean;
  locationEnabled?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onExit?: () => void;
  onSaved?: (routeVersionId: string) => Promise<void> | void;
  routingEnabled?: boolean;
  tenantId: string;
  workspaceTitle?: string;
}>;

export function RoutePlanner({
  draftKey,
  exitLabel = 'Back to routes',
  initialAutoRouting = true,
  initialCenter = BANGKOK_CENTER,
  initialPlan = null,
  initialToolRailCompact = false,
  initialWaypoints = [],
  isFullscreen = true,
  locationEnabled = true,
  onFullscreenChange,
  onExit,
  onSaved,
  routingEnabled = true,
  tenantId,
  workspaceTitle = 'Create route',
}: RoutePlannerProps) {
  const posthog = useAnalytics();
  const routeSearchEnabled = useReleaseFeatureFlag('routeSearch') === true;
  const routeSharingEnabled = useReleaseFeatureFlag('routeSharing') === true;
  const insets = useSafeAreaInsets();
  const [center, setCenter] = React.useState<RouteCoordinate>(initialCenter);
  const [waypoints, setWaypoints] = React.useState<RouteWaypoint[]>([...initialWaypoints]);
  const [history, setHistory] = React.useState<RouteWaypoint[][]>([]);
  const [plan, setPlan] = React.useState<ComputedRoutePlan | null>(initialPlan);
  const [autoRouting, setAutoRouting] = React.useState(initialAutoRouting);
  const [routingProfile, setRoutingProfile] = React.useState<RouteRoutingProfile>('foot-walking');
  const [units, setUnits] = React.useState<RouteUnitSystem>('metric');
  const [mapLayer, setMapLayer] = React.useState<RouteMapLayer>('standard');
  const [routeName, setRouteName] = React.useState('');
  const [isToolRailCompact, setIsToolRailCompact] = React.useState(initialToolRailCompact);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [isSavePromptOpen, setIsSavePromptOpen] = React.useState(false);
  const [isClearPromptOpen, setIsClearPromptOpen] = React.useState(false);
  const [isRouting, setIsRouting] = React.useState(false);
  const [isLocating, setIsLocating] = React.useState(locationEnabled);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const draftHydrated = React.useRef(!draftKey);
  const straightPlan = waypoints.length >= 2 ? straightLineRoutePlan(waypoints) : null;
  const activePlan = autoRouting ? plan : straightPlan;
  const previewPlan = activePlan;
  const distance = formatPlannerDistanceSignificant(previewPlan?.distanceM ?? 0, units);
  const bottomOverlayOffset = isFullscreen
    ? Math.max(insets.bottom, 12)
    : GUIDANCE_PANEL_HEIGHT + Math.max(insets.bottom, 12);

  React.useEffect(() => {
    if (!draftKey) return;
    let active = true;
    void loadRoutePlannerDraft(draftKey)
      .then((draft) => {
        if (!active || !draft) return;
        setAutoRouting(draft.autoRouting);
        setCenter(draft.center);
        setMapLayer(draft.mapLayer);
        setPlan(draft.plan);
        setRouteName(draft.routeName);
        setRoutingProfile(draft.routingProfile);
        setUnits(draft.units);
        setWaypoints([...draft.waypoints]);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) draftHydrated.current = true;
      });
    return () => {
      active = false;
    };
  }, [draftKey]);

  React.useEffect(() => {
    if (!draftKey || !draftHydrated.current) return;
    const timeout = setTimeout(() => {
      if (waypoints.length === 0) {
        void clearRoutePlannerDraft(draftKey).catch(() => undefined);
        return;
      }
      void saveRoutePlannerDraft(draftKey, {
        autoRouting,
        center,
        mapLayer,
        plan: activePlan,
        routeName,
        routingProfile,
        units,
        updatedAt: systemNow().toISOString(),
        waypoints,
      }).catch(() => undefined);
    }, 250);
    return () => clearTimeout(timeout);
  }, [
    activePlan,
    autoRouting,
    center,
    draftKey,
    mapLayer,
    routeName,
    routingProfile,
    units,
    waypoints,
  ]);

  const locateUser = React.useCallback(async () => {
    if (!locationEnabled) return;
    setIsLocating(true);
    try {
      const permission = await Location.requestForegroundPermissionsAsync();
      if (!permission.granted) return;
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.Balanced,
      });
      setCenter({ latitude: location.coords.latitude, longitude: location.coords.longitude });
    } catch {
      setError('Your location is unavailable. Search or move the map to choose an area.');
    } finally {
      setIsLocating(false);
    }
  }, [locationEnabled]);

  React.useEffect(() => {
    if (!locationEnabled) return;
    let active = true;
    void Location.requestForegroundPermissionsAsync()
      .then(async (permission) => {
        if (!active || !permission.granted) return;
        const location = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });
        if (active) {
          setCenter({ latitude: location.coords.latitude, longitude: location.coords.longitude });
        }
      })
      .catch(() => {
        if (active) {
          setError('Your location is unavailable. Search or move the map to choose an area.');
        }
      })
      .finally(() => {
        if (active) setIsLocating(false);
      });
    return () => {
      active = false;
    };
  }, [locationEnabled]);

  React.useEffect(() => {
    if (!autoRouting || !routingEnabled || waypoints.length < 2) return;
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsRouting(true);
      try {
        const nextPlan = await computeRoutePlan(
          tenantId,
          waypoints,
          routingProfile,
          controller.signal
        );
        if (!controller.signal.aborted) {
          setPlan(nextPlan);
          setError(null);
        }
      } catch (routeError) {
        if (!(routeError instanceof Error && routeError.name === 'AbortError')) {
          setError(routeError instanceof Error ? routeError.message : 'Could not calculate route.');
        }
      } finally {
        if (!controller.signal.aborted) setIsRouting(false);
      }
    }, 450);
    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [autoRouting, routingEnabled, routingProfile, tenantId, waypoints]);

  function commitWaypoints(next: RouteWaypoint[]) {
    setHistory((current) => [...current.slice(-19), waypoints]);
    setWaypoints(next);
    setPlan((current) => (autoRouting && next.length >= 2 ? current : null));
    setIsRouting(autoRouting && routingEnabled && next.length >= 2);
    setError(null);
  }

  function addWaypoint(coordinate: RouteCoordinate) {
    if (waypoints.length >= MAX_ROUTE_WAYPOINTS) {
      setError(`Routes support up to ${MAX_ROUTE_WAYPOINTS} waypoints.`);
      return;
    }
    commitWaypoints([
      ...waypoints,
      { ...coordinate, id: `waypoint-${systemNowMs()}-${waypoints.length + 1}` },
    ]);
  }

  function movePoint(id: string, coordinate: RouteCoordinate) {
    commitWaypoints(
      waypoints.map((waypoint) => (waypoint.id === id ? { ...waypoint, ...coordinate } : waypoint))
    );
  }

  function undo() {
    const previous = history.at(-1);
    if (!previous) return;
    setWaypoints(previous);
    setPlan((current) => (autoRouting && previous.length >= 2 ? current : null));
    setIsRouting(autoRouting && routingEnabled && previous.length >= 2);
    setError(null);
    setHistory((current) => current.slice(0, -1));
  }

  function changeAutoRouting(nextAutoRouting: boolean) {
    setAutoRouting(nextAutoRouting);
    setPlan(null);
    setIsRouting(false);
    setError(null);
  }

  function changeRoutingProfile(nextProfile: RouteRoutingProfile) {
    setRoutingProfile(nextProfile);
    setPlan(null);
    setIsRouting(false);
    setError(null);
  }

  function cycleMapLayer() {
    const index = MAP_LAYERS.indexOf(mapLayer);
    setMapLayer(MAP_LAYERS[(index + 1) % MAP_LAYERS.length]);
  }

  function toggleUnits() {
    setUnits((current) => (current === 'metric' ? 'imperial' : 'metric'));
  }

  async function saveRoute() {
    if (!activePlan || !routeName.trim()) {
      setError('Add at least two pins and give the route a name.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const saved = await savePlannedRoute({ name: routeName, plan: activePlan, tenantId });
      posthog.capture('route_saved', {
        routing_profile: routingProfile,
        waypoint_count: waypoints.length,
      });
      if (draftKey) await clearRoutePlannerDraft(draftKey);
      setIsSavePromptOpen(false);
      await onSaved?.(saved.version.id);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save route.');
    } finally {
      setIsSaving(false);
    }
  }

  async function exportGpx(action: 'download' | 'share') {
    if (!activePlan) return;
    setIsExporting(true);
    setError(null);
    try {
      const name = routeName.trim() || '6am route';
      if (action === 'download') {
        await downloadRouteGpx(activePlan.geometry, name);
      } else {
        await shareRouteGpx(activePlan.geometry, name);
      }
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : 'Could not export GPX.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <View className="relative flex-1 overflow-hidden bg-muted">
      <RoutePlannerMap
        center={center}
        mapLayer={mapLayer}
        onAddWaypoint={addWaypoint}
        onMoveWaypoint={movePoint}
        plan={previewPlan}
        units={units}
        waypoints={waypoints}
      />

      <View className="absolute left-3 flex-row gap-2" style={{ top: Math.max(insets.top, 12) }}>
        {onExit && isFullscreen ? (
          <MapIconButton accessibilityLabel={exitLabel} onPress={onExit}>
            <Icon as={XIcon} className="size-5 text-white" />
          </MapIconButton>
        ) : null}
        <MapIconButton
          accessibilityLabel="Center on my location"
          disabled={isLocating}
          onPress={() => void locateUser()}
        >
          {isLocating ? (
            <ActivityIndicator color={BRAND.color.white} />
          ) : (
            <Icon as={LocateFixedIcon} className="size-5 text-white" />
          )}
        </MapIconButton>
        <MapIconButton
          accessibilityLabel="Undo waypoint change"
          disabled={history.length === 0}
          onPress={undo}
        >
          <Icon as={RotateCcwIcon} className="size-5 text-white" />
        </MapIconButton>
        <MapIconButton
          accessibilityLabel={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
          onPress={() => onFullscreenChange?.(!isFullscreen)}
        >
          <Icon as={isFullscreen ? Minimize2Icon : Maximize2Icon} className="size-5 text-white" />
        </MapIconButton>
      </View>

      <Pressable
        accessibilityLabel={`Distance ${distance.value} ${distance.unit}. Show ${
          units === 'metric' ? 'miles' : 'kilometers'
        }`}
        accessibilityRole="button"
        className="absolute right-3 min-w-28 rounded-2xl border border-white/20 bg-neutral-950/95 px-4 py-3"
        onPress={toggleUnits}
        style={{ top: Math.max(insets.top, 12) }}
      >
        <Text variant="label" className="text-white/70" style={{ letterSpacing: 1.6 }}>
          Distance
        </Text>
        <View className="flex-row items-baseline justify-end gap-1.5">
          <Text
            selectable
            className="font-mono text-2xl font-bold text-white"
            style={{ fontVariant: ['tabular-nums'], letterSpacing: -0.8 }}
          >
            {distance.value}
          </Text>
          <Text variant="label" className="text-white">
            {distance.unit}
          </Text>
        </View>
        {isRouting ? <ActivityIndicator color={BRAND.color.orange} size="small" /> : null}
      </Pressable>

      {error ? (
        <View
          className="absolute left-3 max-w-72 rounded-xl border border-red-300/30 bg-neutral-950/95 px-3 py-2"
          style={{ bottom: bottomOverlayOffset }}
        >
          <Text selectable className="text-sm text-white">
            {error}
          </Text>
        </View>
      ) : null}

      {routeSearchEnabled && isSearchOpen ? (
        <View
          className="absolute right-3 w-72 gap-2 rounded-2xl border border-white/20 bg-neutral-950/95 p-3"
          style={{ top: Math.max(insets.top, 12) + 88 }}
        >
          <View className="flex-row items-center justify-between">
            <Text variant="label" className="text-white">
              Find a place
            </Text>
            <Pressable
              accessibilityLabel="Close place search"
              className="size-9 items-center justify-center rounded-lg active:bg-white/10"
              onPress={() => setIsSearchOpen(false)}
            >
              <Icon as={XIcon} className="size-4 text-white" />
            </Pressable>
          </View>
          <RoutePlaceSearch
            focus={center}
            onSelect={(place) => {
              setCenter({ latitude: place.latitude, longitude: place.longitude });
              setIsSearchOpen(false);
            }}
            tone="dark"
          />
        </View>
      ) : null}

      <View className="absolute right-3" style={{ bottom: bottomOverlayOffset }}>
        <RouteToolRail
          autoRouting={autoRouting}
          canClear={waypoints.length > 0}
          canUseRoute={Boolean(activePlan) && !isRouting}
          compact={isToolRailCompact}
          isExporting={isExporting}
          isSaving={isSaving}
          mapLayer={mapLayer}
          onAutoRoutingChange={() => changeAutoRouting(!autoRouting)}
          onClear={() => setIsClearPromptOpen(true)}
          onCompactChange={() => setIsToolRailCompact((current) => !current)}
          onDownload={() => void exportGpx('download')}
          onMapLayerChange={cycleMapLayer}
          onRoutingProfileChange={() =>
            changeRoutingProfile(
              routingProfile === 'foot-walking' ? 'cycling-road' : 'foot-walking'
            )
          }
          onSave={() => setIsSavePromptOpen(true)}
          onSearch={() => setIsSearchOpen((current) => !current)}
          onShare={() => void exportGpx('share')}
          onUnitsChange={toggleUnits}
          routingProfile={routingProfile}
          searchEnabled={routeSearchEnabled}
          sharingEnabled={routeSharingEnabled}
          units={units}
        />
      </View>

      {!isFullscreen ? (
        <RouteCreatorGuidance
          exitLabel={exitLabel}
          onExit={onExit}
          style={{ paddingBottom: Math.max(insets.bottom, 12) }}
          title={workspaceTitle}
        />
      ) : null}

      <SaveRouteDialog
        isSaving={isSaving}
        name={routeName}
        onCancel={() => setIsSavePromptOpen(false)}
        onNameChange={setRouteName}
        onSave={() => void saveRoute()}
        visible={isSavePromptOpen}
      />
      <ClearRouteDialog
        onCancel={() => setIsClearPromptOpen(false)}
        onConfirm={() => {
          setIsClearPromptOpen(false);
          if (draftKey) void clearRoutePlannerDraft(draftKey).catch(() => undefined);
          commitWaypoints([]);
        }}
        visible={isClearPromptOpen}
      />
    </View>
  );
}

function RouteCreatorGuidance({
  exitLabel,
  onExit,
  style,
  title,
}: Readonly<{
  exitLabel: string;
  onExit?: () => void;
  style: React.ComponentProps<typeof View>['style'];
  title: string;
}>) {
  return (
    <View
      className="absolute inset-x-0 bottom-0 gap-3 border-t border-border bg-background px-5 pt-4"
      style={[{ minHeight: GUIDANCE_PANEL_HEIGHT }, style]}
    >
      <View className="flex-row items-center justify-between gap-4">
        <View className="min-w-0 flex-1 gap-0.5">
          <Text variant="label" className="text-primary">
            Route creator
          </Text>
          <Text numberOfLines={1} variant="large">
            {title}
          </Text>
        </View>
        {onExit ? (
          <Button onPress={onExit} size="sm" variant="outline">
            <Icon as={ArrowLeftIcon} className="size-4 text-foreground" />
            <Text>{exitLabel}</Text>
          </Button>
        ) : null}
      </View>
      <View className="gap-1.5">
        <Text className="text-sm text-muted-foreground">1. Tap the map to place your start.</Text>
        <Text className="text-sm text-muted-foreground">
          2. Add points to extend the route; Auto follows mapped paths.
        </Text>
        <Text className="text-sm text-muted-foreground">
          3. Review the distance, then save or download your GPX.
        </Text>
      </View>
    </View>
  );
}

function MapIconButton({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      size="icon"
      variant="secondary"
      className="border-white/15 bg-neutral-950/95"
      {...props}
    >
      {children}
    </Button>
  );
}

type RouteToolRailProps = Readonly<{
  autoRouting: boolean;
  canClear: boolean;
  canUseRoute: boolean;
  compact: boolean;
  isExporting: boolean;
  isSaving: boolean;
  mapLayer: RouteMapLayer;
  onAutoRoutingChange: () => void;
  onClear: () => void;
  onCompactChange: () => void;
  onDownload: () => void;
  onMapLayerChange: () => void;
  onRoutingProfileChange: () => void;
  onSave: () => void;
  onSearch: () => void;
  onShare: () => void;
  onUnitsChange: () => void;
  routingProfile: RouteRoutingProfile;
  searchEnabled: boolean;
  sharingEnabled: boolean;
  units: RouteUnitSystem;
}>;

function RouteToolRail({
  autoRouting,
  canClear,
  canUseRoute,
  compact,
  isExporting,
  isSaving,
  mapLayer,
  onAutoRoutingChange,
  onClear,
  onCompactChange,
  onDownload,
  onMapLayerChange,
  onRoutingProfileChange,
  onSave,
  onSearch,
  onShare,
  onUnitsChange,
  routingProfile,
  searchEnabled,
  sharingEnabled,
  units,
}: RouteToolRailProps) {
  const profileLabel = routingProfile === 'foot-walking' ? 'Foot' : 'Road';
  const unitsLabel = units === 'metric' ? 'KM' : 'MI';

  return (
    <View
      className={cn(
        'rounded-xl border border-white/20 bg-neutral-950/95 p-1.5 shadow-lg',
        compact ? 'w-14' : 'w-44'
      )}
    >
      <RouteToolButton
        compact={compact}
        icon={compact ? ChevronLeftIcon : ChevronRightIcon}
        label={compact ? 'Expand tools' : 'Minimize'}
        onPress={onCompactChange}
      />
      <RouteToolDivider />
      {searchEnabled ? (
        <RouteToolButton compact={compact} icon={SearchIcon} label="Search" onPress={onSearch} />
      ) : null}
      <RouteToolButton
        active={autoRouting}
        compact={compact}
        icon={RouteIcon}
        label={`Auto · ${autoRouting ? 'On' : 'Off'}`}
        onPress={onAutoRoutingChange}
      />
      <RouteToolButton
        compact={compact}
        disabled={!autoRouting}
        icon={FootprintsIcon}
        label={`Route · ${profileLabel}`}
        onPress={onRoutingProfileChange}
      />
      <RouteToolButton
        compact={compact}
        icon={RulerIcon}
        label={`Units · ${unitsLabel}`}
        onPress={onUnitsChange}
      />
      <RouteToolButton
        compact={compact}
        icon={Layers3Icon}
        label={`Map · ${mapLayer}`}
        onPress={onMapLayerChange}
      />
      <RouteToolDivider />
      <RouteToolButton
        compact={compact}
        disabled={!canUseRoute || isSaving}
        icon={SaveIcon}
        label={isSaving ? 'Saving' : 'Save'}
        onPress={onSave}
      />
      {sharingEnabled ? (
        <>
          <RouteToolButton
            compact={compact}
            disabled={!canUseRoute || isExporting}
            icon={Share2Icon}
            label="Share"
            onPress={onShare}
          />
          <RouteToolButton
            compact={compact}
            disabled={!canUseRoute || isExporting}
            icon={DownloadIcon}
            label="Download GPX"
            onPress={onDownload}
          />
        </>
      ) : null}
      <RouteToolDivider />
      <RouteToolButton
        compact={compact}
        danger
        disabled={!canClear}
        icon={EraserIcon}
        label="Clear"
        onPress={onClear}
      />
    </View>
  );
}

function RouteToolButton({
  active = false,
  compact,
  danger = false,
  disabled,
  icon,
  label,
  onPress,
}: Readonly<{
  active?: boolean;
  compact: boolean;
  danger?: boolean;
  disabled?: boolean;
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  onPress: () => void;
}>) {
  let iconColor = 'text-white';
  if (active) iconColor = 'text-primary';
  if (danger) iconColor = 'text-red-400';

  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      className={cn(
        'min-h-10 flex-row items-center rounded-lg px-2 active:bg-white/10',
        compact ? 'justify-center' : 'gap-2.5',
        active && 'bg-primary/20',
        disabled && 'opacity-40'
      )}
      disabled={disabled}
      onPress={onPress}
    >
      <Icon as={icon} className={cn('size-5', iconColor)} />
      {!compact ? (
        <Text
          numberOfLines={1}
          variant="label"
          className={cn('flex-1 text-xs', danger ? 'text-red-400' : 'text-white')}
        >
          {label}
        </Text>
      ) : null}
    </Pressable>
  );
}

function RouteToolDivider() {
  return <View className="mx-1 my-1 h-px bg-white/20" />;
}

function SaveRouteDialog({
  isSaving,
  name,
  onCancel,
  onNameChange,
  onSave,
  visible,
}: Readonly<{
  isSaving: boolean;
  name: string;
  onCancel: () => void;
  onNameChange: (name: string) => void;
  onSave: () => void;
  visible: boolean;
}>) {
  return (
    <Modal animationType="fade" onRequestClose={onCancel} transparent visible={visible}>
      <View className="flex-1 items-center justify-center bg-black/60 px-6">
        <View className="w-full max-w-sm gap-4 rounded-2xl border border-white/20 bg-neutral-950 p-4">
          <View className="flex-row items-center justify-between">
            <Text variant="label" className="text-white">
              Save route
            </Text>
            <Pressable
              accessibilityLabel="Cancel saving route"
              className="size-9 items-center justify-center rounded-lg active:bg-white/10"
              onPress={onCancel}
            >
              <Icon as={XIcon} className="size-4 text-white" />
            </Pressable>
          </View>
          <Input
            accessibilityLabel="Route name"
            autoFocus
            maxLength={120}
            onChangeText={onNameChange}
            placeholder="Route name"
            placeholderTextColor="rgba(255,255,255,0.5)"
            value={name}
          />
          <View className="flex-row justify-end gap-2">
            <Button disabled={isSaving} onPress={onCancel} size="sm" variant="outline">
              <Text>Cancel</Text>
            </Button>
            <Button disabled={isSaving || !name.trim()} onPress={onSave} size="sm">
              {isSaving ? <ActivityIndicator color={BRAND.color.white} size="small" /> : null}
              <Text>{isSaving ? 'Saving' : 'Save route'}</Text>
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function ClearRouteDialog({
  onCancel,
  onConfirm,
  visible,
}: Readonly<{
  onCancel: () => void;
  onConfirm: () => void;
  visible: boolean;
}>) {
  return (
    <Modal animationType="fade" onRequestClose={onCancel} transparent visible={visible}>
      <View className="flex-1 items-center justify-center bg-black/60 px-6">
        <View className="w-full max-w-sm gap-4 rounded-2xl border border-white/20 bg-neutral-950 p-4">
          <View className="gap-1">
            <Text variant="label" className="text-white">
              Clear route?
            </Text>
            <Text className="text-sm text-white/75">
              Every plotted point will be removed. This cannot be undone.
            </Text>
          </View>
          <View className="flex-row justify-end gap-2">
            <Button onPress={onCancel} size="sm" variant="outline">
              <Text>Keep route</Text>
            </Button>
            <Button onPress={onConfirm} size="sm" variant="destructive">
              <Text>Clear route</Text>
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}
